import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import InputField from "components/fields/InputField";
import { apiGet, apiPost } from "lib/apiClient";

/** Step 2 of partner onboarding by invite (step 1: an admin sends the link — see
 *  superadmin-portal's clients-list "Invite partner" modal). Fully public: whoever holds
 *  this link has no account yet, so there's nothing to authenticate against. */
export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("This invitation link is missing its token.");
      return;
    }
    apiGet(`/client-invitations/${encodeURIComponent(token)}`)
      .then((res) => setInvitation(res.invitation))
      .catch((err) => setLoadError(err.message || "This invitation link is invalid."));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await apiPost(`/client-invitations/${encodeURIComponent(token)}/accept`, { password });
      setDone(true);
      setTimeout(() => navigate("/auth/sign-in", { replace: true }), 2500);
    } catch (err) {
      setError(err.message || "Could not activate your account.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex w-full">
      <div className="w-full">
        {loadError ? (
          <>
            <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">Invalid invitation</h4>
            <p className="mb-4 text-sm text-muted-foreground">{loadError}</p>
            <Link to="/auth/sign-in" className="text-sm font-medium text-link hover:underline">
              Go to sign in
            </Link>
          </>
        ) : done ? (
          <>
            <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">You're all set</h4>
            <p className="mb-4 text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </>
        ) : !invitation ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">Activate your account</h4>
            <p className="mb-6 text-sm text-muted-foreground">
              {invitation.name}, set a password for <span className="font-semibold">{invitation.businessName}</span> to access the partner
              portal.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <InputField
                variant="auth"
                extra="mb-3"
                label="New password*"
                placeholder="Min. 10 characters"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <InputField
                variant="auth"
                extra="mb-3"
                label="Confirm password*"
                placeholder="Re-enter your password"
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              {error && <p className="mb-4 text-sm font-medium text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-sm bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
              >
                {submitting ? "Activating…" : "Set password & activate"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
